// components/RestaurantForm.tsx
import React, { useState, useEffect, useRef, type Dispatch, type SetStateAction } from "react";
import type { RestaurantFormData } from "../restaurant.types";
import BasicInfoStep from "./BasicInfoStep";
import LocationStep from "./LocationStep";
import CuisineStep from "./CuisineStep";
import HoursStep from "./HoursStep";
import GalleryStep from "./GalleryStep";
import BudgetStep from "./BudgetStep";
import { createRestaurant, updateRestaurant } from "../../../lib/api/restaurant.apis";
import { useFormError } from "../Context/FormErrorContext";
import { ThreeDots } from "react-loader-spinner";
import { useNavigate } from "react-router-dom";

interface RestaurantFormProps {
  initialData: Partial<RestaurantFormData>;
}

const RestaurantForm: React.FC<RestaurantFormProps> = ({
  initialData,
}) => {
  const [newRestaurantId, setNewRestaurantId ] = useState<string | null>()
  const [currentStep, setCurrentStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [disable, setDisable] = useState(true);
  const [formData, setFormData] = useState<RestaurantFormData>({
    name: initialData?.name ?? "",
    description: initialData?.description ?? "",
    location: initialData?.location ?? {
      address: "",
      area: "",
      city: "",
      geo: { type: "Point", coordinates: [0, 0] },
    },
    cuisine: initialData?.cuisine ?? [],
    specialFeatures: initialData?.specialFeatures ?? [],
    typeOfFood: initialData?.typeOfFood ?? [],
    experience: initialData?.experience ?? [],
    tags: initialData?.tags ?? [],
    priceRange: initialData?.priceRange ?? { min: 0, max: 0 },
    budgetLevel: initialData?.budgetLevel ?? "$$",
    openingHours: initialData?.openingHours ?? {
      monday: "11:00 - 23:00",
      tuesday: "11:00 - 23:00",
      wednesday: "11:00 - 23:00",
      thursday: "11:00 - 23:00",
      friday: "11:00 - 23:00",
      saturday: "11:00 - 23:00",
      sunday: "11:00 - 23:00",
    },
    gallery: initialData?.gallery ?? {
      cover: null,
      food: [],
      ambiance: [],
      menu: [],
    },
  });
  const { formError, setFormError, clearFormError } = useFormError();
  const navigate = useNavigate();

  useEffect(() => {
    let shouldDisable = false;

    switch (currentStep) {
      case 0: // Cuisine & Tags Step
        const hasAnyDescriptors =
          (formData.cuisine?.length ?? 0) > 0 ||
          (formData.tags?.length ?? 0) > 0 ||
          (formData.typeOfFood?.length ?? 0) > 0 ||
          (formData.experience?.length ?? 0) > 0;
        shouldDisable = !hasAnyDescriptors;
        break;

      case 1: // Basic Info Step
        shouldDisable = !formData.name || !formData.description;
        break;

      case 2: // Location Step
        shouldDisable =
          !formData.location?.address ||
          !formData.location?.area ||
          !formData.location?.geo?.coordinates?.length;
        break;

      case 3: // Opening Hours Step
        shouldDisable = !formData.openingHours;
        break;

      case 4: // Budget Step
        shouldDisable = !formData.budgetLevel;
        break;

      case 5: // Gallery Step
        const uploadingInGallery =
          formData.gallery?.cover?.status === "uploading" ||
          formData.gallery?.ambiance?.some(item => item.status === "uploading") ||
          formData.gallery?.food?.some(item => item.status === "uploading") ||
          formData.gallery?.menu?.some(item => item.status === "uploading");

        shouldDisable = uploadingInGallery;
        break;

      default:
        shouldDisable = false;
    }

    setDisable(shouldDisable);
  }, [currentStep, formData]);

  const [loading, setLoading] = useState(false);

  const draftId = useRef(
    (initialData as any)?.id || `draft-${crypto.randomUUID()}`
  );
  const saveToLocal = (data: RestaurantFormData) => {
    const saved = localStorage.getItem("local-restaurants");
    const drafts = saved ? JSON.parse(saved) : [];

    const updatedDraft = {
      id: draftId.current,
      ...data,
    };

    const existingIndex = drafts.findIndex(
      (r: any) => r.id === draftId.current
    );

    if (existingIndex >= 0) drafts[existingIndex] = updatedDraft;
    else drafts.push(updatedDraft);

    localStorage.setItem("local-restaurants", JSON.stringify(drafts));
    return drafts
  };
  const deleteDraft = (id: string) => {
    const saved = localStorage.getItem("local-restaurants");
    if (!saved) return;

    const drafts = JSON.parse(saved);
    const filtered = drafts.filter((r: any) => r.id !== id);
    localStorage.setItem("local-restaurants", JSON.stringify(filtered));
  };

  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({ ...prev, ...initialData }));
    }
  }, [initialData]);

  const steps = [
    { title: "Cuisine & Tags", component: CuisineStep },
    { title: "Basic Info", component: BasicInfoStep },
    { title: "Location", component: LocationStep },
    { title: "Opening Hours", component: HoursStep },
    { title: "budgetLevel", component: BudgetStep },
    { title: "Gallery", component: GalleryStep },
  ];

  const stepHeadings = [
    "How would you describe your gem’s vibe and cuisine?",
    "Now, how do yo describe your gem?",
    "Where can we find your hidden gem?",
    "When are you open?",
    "Now, how would you describe your pricing?",
    "Showcase your space with best photos",
  ];

  const updateFormData = (data: Partial<RestaurantFormData>) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        ...data,
      };
      if (data.location) {
        updated.location = {
          ...prev.location,
          ...data.location,
          geo: {
            ...prev.location.geo,
            ...data.location.geo,
            coordinates:
              data.location.geo?.coordinates ??
              prev.location.geo.coordinates,
          },
        };
      }
      
      // Special handling for gallery to properly merge arrays
      if (data.gallery) {
        updated.gallery = {
          ...prev.gallery,
          ...data.gallery,
          
          // Ensure food and ambiance arrays are properly replaced, not merged
          ...(data.gallery.food !== undefined && { food: data.gallery.food }),
          ...(data.gallery.ambiance !== undefined && { ambiance: data.gallery.ambiance }),
          ...(data.gallery.menu !== undefined && { menu: data.gallery.menu }),
          ...(data.gallery.cover !== undefined && { cover: data.gallery.cover }),
        };
      }
      
      return updated;
    });
  };

  const handleSubmit = async (formData) => {
    clearFormError();
    setLoading(true);
    try {
      const restaurant = await createRestaurant(formData);
      setNewRestaurantId(restaurant._id);
      deleteDraft(draftId.current);
    } catch (err) {
      const message = err.message || "Something went wrong while saving your restaurant.";
      setFormError(message);

      const drafts = JSON.parse(localStorage.getItem("local-restaurants") || "[]");

      drafts.push({ ...formData, id: `draft-${crypto.randomUUID()}` });

      localStorage.setItem("local-restaurants", JSON.stringify(drafts));
      throw err;
    } finally {
      setLoading(false);
    }
    
  };

  const CurrentStepComponent = steps[currentStep].component;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;
  const isSecLastStep = currentStep === steps.length - 2;

  const nextStep = async () => {
    clearFormError();
    saveToLocal(formData);

    if (isSecLastStep) {
      try {
        setLoading(true);
        await handleSubmit(formData);
      } catch (error: any) {
        console.error("❌ Server error:", error);
        setFormError(error.message || "Something went wrong creating the restaurant.");
        return; // stop step change
      } finally {
        setLoading(false);
      }
    }

    if (currentStep < steps.length - 1) {
      setIsAnimating(false);
      setTimeout(() => {
        setCurrentStep((prev) => prev + 1);
        setIsAnimating(true);
      }, 50);
    }
  };

  const prevStep = () => {
    saveToLocal(formData);
    if (currentStep > 0) {
      setIsAnimating(false);
      setTimeout(() => {
        setCurrentStep((prev) => prev - 1);
        setIsAnimating(true);
      }, 50);
    }
  };

  const handleCancel = () => {
    deleteDraft(draftId.current);
    navigate("/dashboard");
  }

  const handlePublish = async () => {
    setLoading(true)
    setDisable(true)
    try {
      const updatedData = {
        ...formData,
        isPublished: true,
      }

      const response = await updateRestaurant(newRestaurantId, updatedData);

      setLoading(false);
      navigate("/dashboard");
      deleteDraft(draftId.current);
    } catch (error) {
      setLoading(false);
      console.error(error);
      setDisable(false)
    } finally {
      navigate("/dashboard");
      setLoading(false)
    }
  }

  useEffect(() => {
    setIsAnimating(true);
  }, [currentStep]);

  return (
    <div className="bg-white">
      {/* Main Content */}
      <main className="max-w-5xl mx-auto sm:px-2 lg:px-8 py-2">
        {/* Current Step Heading */}
        <div className="text mb-2 overflow-hidden">
          <h1
            className={`text-3xl font-bold text-[#111827] ${
              isAnimating ? "fade-in" : "opacity-0"
            }`}
          >
            {stepHeadings[currentStep]}
          </h1>
          {currentStep == 1 && (
            <p className="text-[#5E503F] ">
              Name and why your restaurant special?
            </p>
          )}
        </div>

        {/* Scrollable Form Container */}
        <div className="rounded-2xl overflow-hidden mb-2">
          <div
            className={`max-h-[60vh] overflow-y-auto p-2 ${
              isAnimating ? "fade-in-scale" : "opacity-0"
            }`}
          >
            <CurrentStepComponent
              formData={formData}
              updateFormData={updateFormData}
              isActive={isAnimating}
              setDisableNext={setDisable}
            />
          </div>
        </div>

        {/* Enhanced Progress Bar with Connecting Lines */}
        <div>
          <div className="mt-2">
            <div className="flex items-center bg-transparent justify-between relative">
              {/* Background Line */}
              <div className="absolute top-4 left-0 right-0 h-0.5 bg-[#FFDDA6] bg-opacity-50 z-10" />

              {/* Progress Line */}
              <div
                className="absolute top-4 left-0 h-0.5 bg-[#FFD166] z-10 transition-all duration-500 ease-out"
                style={{
                  width: `${(currentStep / (steps.length - 1)) * 100}%`,
                }}
              />

              {/* Steps with Connecting Lines */}
              {steps.map((step, index) => (
                <div
                  key={step.title}
                  className="relative z-20 flex flex-col items-center flex-1"
                >
                  {/* Step Circle */}
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold transition-all duration-300 border-2 ${
                      index <= currentStep
                        ? "bg-[#FFD166] border-[#FFD166] text-[#111827] shadow-sm"
                        : "bg-white border-[#FFDDA6] text-[#5E503F]"
                    }`}
                  >
                    {index + 1}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-4 border-[#FFDDA6]">
            {isFirstStep ? (
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 py-3 border border-[#FFDDA6] text-[#5E503F] rounded-lg hover:bg-[#FFDDA6] transition-all duration-300 hover:scale-105 active:scale-95"
              >
                Cancel
              </button>
            ) : (
              <button
                type="button"
                onClick={prevStep}
                disabled={disable}
                className="px-6 py-3 border border-[#FFDDA6] text-[#5E503F] rounded-lg hover:bg-[#FFDDA6] transition-all duration-300 hover:scale-105 active:scale-95"
              >
                Previous
              </button>
            )}
            {isLastStep ? (
              <button
                type="button"
                onClick={handlePublish}
                disabled={loading || disable}
                className="px-6 py-3 bg-[#FFD166] text-[#111827] font-semibold rounded-lg hover:bg-[#E1AD01] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-95 shadow-md hover:shadow-lg"
              >
                {loading ? <ThreeDots height={10} width={30} color="white" /> : "Publish" }
              </button>
            ) : (
              <button
                type="button"
                onClick={nextStep}
                disabled={disable}
                className={`px-6 bg-[#FFD166] text-[#111827] font-semibold rounded-lg hover:bg-[#E1AD01] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:scale-105 active:scale-95 shadow-md hover:shadow-lg`}
              >
                {loading ? <ThreeDots height={10} width={30} color="white" /> : "Next" }
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default RestaurantForm;
